import { orchestrationApi } from '../../api/orchestrations/orchestrationApi';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IPostJobsInfo } from 'interfaces/postJobsInfo.interface';
import {
  IActiveFilter,
  IDashboardState,
  IStatusCounters,
  IUpdateOrchestrationStatus,
  IUserValue,
  IAddJob,
  IOrchestrationsServerResponse,
} from 'interfaces/dashboard/dashboardSlice.interface';
import {
  JOBS_OWNER_FILTER,
  ORCHESTRATION_FILTERS,
  ORCHESTRATION_STATUSES,
} from '../../utils/common-constants';
import { IOrchestrationRow } from '../../interfaces/dashboard/orchestrationRow.interface';
import { IOrchestrationStatus } from '../../interfaces/dashboard/orchestrationStatus.interface';
import { IPaginatorStatusInterface } from '../../interfaces/paginatorStatus.interface';
import { ORCHESTRATION_FILTER_ITEMS } from '../../pages/Dashboard/Dashboard.constants';
import {
  mapJobToTableData,
  mapResponseJobsToTableData,
} from 'dto/jobToTable/jobToTable';
import { RootState } from 'redux/store';
import { getNameFromEmail } from 'utils/commonFunctions/CommonFunctions';
import { IJobStartedByUser } from 'interfaces/user.interface';
import { IStepInformation } from 'interfaces/orchestration.interface';
import {
  fetchAllOrchestrations,
  postViewLog,
} from 'api/orchestrations/orchestrationsThunk';

const setIsSelected = (
  orchestrations: IOrchestrationRow[],
  selectedOrchestrations: IOrchestrationRow[]
) => {
  return orchestrations?.map((orchestration) => {
    if (
      selectedOrchestrations.find(
        (selected) => selected.id === orchestration.id
      )
    ) {
      return { ...orchestration, isSelected: true };
    } else {
      return orchestration;
    }
  });
};

const calculateCount = (payload: any, status: string): number => {
  const upperCased = status.toUpperCase();
  if (upperCased === ORCHESTRATION_STATUSES.STOPPED) {
    return payload[status] + payload['stopping'] || 0;
  } else if (upperCased === ORCHESTRATION_STATUSES.FINISHED) {
    return payload[status] + payload['finished_error'] || 0;
  } else {
    return payload[status] || 0;
  }
};

const combineFilters = (filter: string): string[] => {
  const upperCased = filter.toUpperCase();
  const result = [upperCased];

  if (upperCased === ORCHESTRATION_STATUSES.STOPPED) {
    result.push('STOPPING');
  } else if (upperCased === ORCHESTRATION_STATUSES.FINISHED) {
    result.push('FINISHED_ERROR');
  }

  return result;
};

export const SLICE_KEY = 'dashboardContainer';

const initialState: IDashboardState = {
  statuses: ORCHESTRATION_FILTER_ITEMS,
  selectedStatus: ORCHESTRATION_FILTER_ITEMS[0],
  loading: true,
  orchestrations: [],
  selectedOrchestrations: [],
  activeFilters: [],
  filterModel: null,
  paginatorInfo: {
    page: 1,
    pageSize: 20,
  },
  filterInfo: {
    jobStatuses: ['ANY'],
    activePage: 1,
    itemsPerPage: 20,
    addJobInfo: true,
    jobsOwner: JOBS_OWNER_FILTER.ALL_JOBS,
  },
  allUsers: [],
  sseOrchestrationsEvents: [],
  orchestrationIdsMap: {},
};

const dashboardSlice = createSlice({
  name: SLICE_KEY,
  initialState,
  reducers: {
    setStatuses: (state, action: PayloadAction<IOrchestrationStatus[]>) => {
      return { ...state, statuses: action.payload };
    },
    setLabelForFirstStatus: (state, action: PayloadAction<string>) => {
      const newStatus = [...state.statuses];
      newStatus[0].title = action.payload;
      state.statuses = newStatus;
    },
    setSelectedStatus: (state, action: PayloadAction<IOrchestrationStatus>) => {
      state.selectedStatus = action.payload;
    },
    setOrchestrations: (state, action: PayloadAction<IOrchestrationRow[]>) => {
      state.orchestrations = action.payload;
    },
    setFilterInfo: (state, action: PayloadAction<IPostJobsInfo>) => {
      return {
        ...state,
        filterInfo: {
          jobStatuses: combineFilters(state.selectedStatus.filterId),
          activePage: state.paginatorInfo.page,
          itemsPerPage: state.paginatorInfo.pageSize,
          addJobInfo: true,
          jobsOwner: action.payload.jobsOwner ?? state.filterInfo?.jobsOwner,
          ...action.payload,
        },
      };
    },
    setFilterModel: (state, action: PayloadAction<any>) => {
      state.filterModel = action.payload;
    },
    composeFilterInfo: (state) => {
      state.filterInfo = {
        ...state.filterInfo,
        ...{
          jobStatuses: combineFilters(state.selectedStatus.filterId),
          activePage: state.paginatorInfo.page,
          itemsPerPage: state.paginatorInfo.pageSize,
        },
      };
    },
    resetSort: (state) => {
      return {
        ...state,
        filterInfo: {
          ...state.filterInfo,
          sortingField: undefined,
          sortingType: undefined,
          ...{
            jobStatuses: combineFilters(state.selectedStatus.filterId),
            activePage: 1,
            itemsPerPage: state.paginatorInfo.pageSize,
          },
        },
      };
    },
    setPaginatorInfo: (
      state,
      action: PayloadAction<IPaginatorStatusInterface>
    ) => {
      state.paginatorInfo = action.payload;
    },
    resetPaginatorInfo: (state) => {
      state.paginatorInfo = { ...initialState.paginatorInfo };
    },
    setSelectedOrchestrations: (
      state,
      action: PayloadAction<IOrchestrationRow[]>
    ) => {
      state.selectedOrchestrations = [...action.payload];
    },
    addSelectedOrchestration: (
      state,
      action: PayloadAction<IOrchestrationRow>
    ) => {
      if (
        !state.selectedOrchestrations?.find(
          (selected) => selected.id === action.payload.id
        )
      ) {
        state.selectedOrchestrations = [
          ...state.selectedOrchestrations,
          action.payload,
        ];
      }
    },
    removeSelectedOrchestration: (
      state,
      action: PayloadAction<IOrchestrationRow>
    ) => {
      state.selectedOrchestrations = state.selectedOrchestrations.filter(
        (selected) => selected.id !== action.payload.id
      );
    },
    setActiveFilters: (state, action: PayloadAction<IActiveFilter[]>) => {
      state.activeFilters = action.payload;
    },
    addActiveFilter: (state, action: PayloadAction<IActiveFilter>) => {
      const filterIndex = state.activeFilters.findIndex(
        (filter) => filter.id === action.payload.id
      );
      if (filterIndex !== -1) {
        state.activeFilters[filterIndex] = action.payload;
      } else {
        state.activeFilters.push(action.payload);
      }
    },
    removeActiveFilter: (state, action: PayloadAction<string>) => {
      state.activeFilters = state.activeFilters.filter(
        (filter) => filter.id !== action.payload
      );
    },
    addOrchestrationToSseQueue: (state, action: PayloadAction<IAddJob>) => {
      const newJob = mapJobToTableData(action.payload.job);
      const newEvent = {
        type: 'addOrchestration' as const,
        data: newJob,
      };

      if (state.filterInfo?.jobsOwner !== 'USER_JOBS') {
        state.sseOrchestrationsEvents.push(newEvent);
      } else {
        if (newJob.user?.id === action.payload.userId) {
          state.sseOrchestrationsEvents.push(newEvent);
        }
      }
    },
    addOrchestrationRemoveLastOne: (
      state,
      action: PayloadAction<IOrchestrationRow>
    ) => {
      // Check if orchestration already exists to avoid duplicates
      const existingIndex = state.orchestrations.findIndex(
        (orch) => orch.id === action.payload.id
      );

      if (existingIndex !== -1) {
        return;
      }

      state.orchestrations.unshift(action.payload);
      const maxItems = state.paginatorInfo.page * 20;

      if (state.orchestrations.length > maxItems) {
        state.orchestrations = state.orchestrations.slice(0, maxItems);
      }
    },
    modifySseOrchestrationEvents: (state, action: PayloadAction<any>) => {
      state.sseOrchestrationsEvents = action.payload;
    },
    updateJobStatus: (
      state,
      action: PayloadAction<IUpdateOrchestrationStatus>
    ) => {
      state.sseOrchestrationsEvents.push({
        type: 'updateStatus',
        data: action.payload,
      });
    },
    updateStepStatus: (state, action: PayloadAction<IStepInformation>) => {
      state.sseOrchestrationsEvents.push({
        type: 'updateStep',
        data: action.payload,
      });
    },
    updateCounters: (state, action: PayloadAction<IStatusCounters>) => {
      const isFilterApplied =
        state.filterModel && Object.keys(state.filterModel).length;
      const isOnlyCounters = !action.payload.jobs;
      if (isFilterApplied && isOnlyCounters) {
        return;
      }
      const itemsToIterate = state.statuses
        ? state.statuses
        : ORCHESTRATION_FILTER_ITEMS;
      state.statuses = itemsToIterate.map((status) => {
        return {
          ...status,
          count: calculateCount(action.payload, status.id),
        };
      });
    },
    updateOrchestrations: (
      state,
      action: PayloadAction<IOrchestrationsServerResponse>
    ) => {
      if (!action.payload.jobs) {
        return {
          ...state,
          loading: false,
        };
      } else {
        // dashboardSlice.caseReducers.updateCounters(state, action as any);
        return {
          ...state,
          orchestrations: setIsSelected(
            mapResponseJobsToTableData(action.payload.jobs),
            state.selectedOrchestrations
          ),
        };
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        loading: action.payload,
      };
    },
    setOrchestrationIdsMap: (state, action: PayloadAction<number[]>) => {
      const idsArray = action.payload;
      const map: Record<number, boolean> = {};
      idsArray.forEach((id) => {
        map[id] = true;
      });
      state.orchestrationIdsMap = map;
    },
    updateExistingOrchestration: (
      state,
      action: PayloadAction<IOrchestrationRow>
    ) => {
      const updatedOrchestration = action.payload;
      const existingIndex = state.orchestrations.findIndex(
        (orch) => orch.id === updatedOrchestration.id
      );

      if (existingIndex !== -1) {
        state.orchestrations[existingIndex] = updatedOrchestration;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllOrchestrations.pending, (state) => {
      const isFirstLoad =
        state.loading === true && state.orchestrations.length === 0;
      return {
        ...state,
        loading: isFirstLoad,
      };
    });
    builder.addCase(fetchAllOrchestrations.rejected, (state) => {
      return {
        ...state,
        loading: false,
      };
    });
    builder.addCase(
      fetchAllOrchestrations.fulfilled,
      (
        state,
        action: PayloadAction<{
          data: IOrchestrationsServerResponse;
          isScrolling: boolean;
        }>
      ) => {
        if (!action.payload) {
          return;
        }
        if (!action.payload.data.jobs) {
          state.loading = false;
          return;
        }

        if (!action.payload.isScrolling) {
          state.orchestrations = setIsSelected(
            mapResponseJobsToTableData(action.payload.data.jobs),
            state.selectedOrchestrations
          );
        } else {
          const newOrchestrations = setIsSelected(
            mapResponseJobsToTableData(action.payload.data.jobs),
            state.selectedOrchestrations
          );
          const existingIds = new Set(state.orchestrations.map((o) => o.id));
          const filteredNewOrchestrations = newOrchestrations.filter(
            (o) => !existingIds.has(o.id)
          );
          state.orchestrations = [
            ...state.orchestrations,
            ...filteredNewOrchestrations,
          ];
        }

        dashboardSlice.caseReducers.updateCounters(state, {
          payload: action.payload.data,
        } as any);

        if (state.selectedStatus) {
          const selectedStatus = state.statuses.find(
            (status) => status.id === state.selectedStatus.id
          );
          if (selectedStatus) {
            state.selectedStatus = selectedStatus;
          }
        } else {
          state.selectedStatus = state.statuses[0];
        }

        state.loading = false;
      }
    );
    builder.addCase(postViewLog.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(postViewLog.fulfilled, (state) => {
      return {
        ...state,
        loading: false,
      };
    });
    builder.addCase(postViewLog.rejected, (state) => {
      return {
        ...state,
        loading: false,
      };
    });

    builder.addMatcher(
      orchestrationApi.endpoints.getUsersAll.matchFulfilled,
      (state, action: PayloadAction<IUserValue[]>) => {
        const uniqueUsers = action.payload.reduce(
          (acc: IJobStartedByUser[], user: IUserValue) => {
            if (!acc.find((u: IJobStartedByUser) => u.id === user.userId)) {
              acc.push({
                id: user.userId,
                name: getNameFromEmail(user.userEmail),
              });
            }
            return acc;
          },
          []
        );
        state.allUsers = uniqueUsers;
      }
    );
  },
});

export const selectFilterStatus = (state: RootState) =>
  state.dashboardContainer.filterInfo;

export const selectOrchestrations = (state: RootState) =>
  state.dashboardContainer.orchestrations;

export const selectPaginationInfo = (state: RootState) =>
  state.dashboardContainer.paginatorInfo;

export const selectFilterModel = (state: RootState) =>
  state.dashboardContainer.filterModel;

export const selectActiveFilter = (state: RootState) =>
  state.dashboardContainer.activeFilters;

export const selectSelectedStatus = (state: RootState) =>
  state.dashboardContainer.selectedStatus;

export const selectStatuses = (state: RootState) =>
  state.dashboardContainer.statuses;
export const selectSelectedOrchestrations = (state: RootState) =>
  state.dashboardContainer.selectedOrchestrations;
export const selectOrchestrationIdsMap = (state: RootState) =>
  state.dashboardContainer.orchestrationIdsMap;

export type SelectFilterInfoType = ReturnType<typeof selectFilterStatus>;
export type SelectOrchestrationsType = ReturnType<typeof selectOrchestrations>;
export type SelectPaginationInfoType = ReturnType<typeof selectPaginationInfo>;
export type SelectFilterModelType = ReturnType<typeof selectFilterModel>;
export type SelectActiveFilterType = ReturnType<typeof selectActiveFilter>;
export type SelectSelectedStatusType = ReturnType<typeof selectSelectedStatus>;
export type SelectStatusesType = ReturnType<typeof selectStatuses>;

export const {
  setStatuses,
  setLabelForFirstStatus,
  setSelectedStatus,
  setOrchestrations,
  setSelectedOrchestrations,
  setFilterInfo,
  setFilterModel,
  addSelectedOrchestration,
  removeSelectedOrchestration,
  addActiveFilter,
  removeActiveFilter,
  setActiveFilters,
  setPaginatorInfo,
  resetPaginatorInfo,
  composeFilterInfo,
  addOrchestrationToSseQueue,
  updateJobStatus,
  updateCounters,
  updateStepStatus,
  resetSort,
  modifySseOrchestrationEvents,
  addOrchestrationRemoveLastOne,
  updateOrchestrations,
  setLoading,
  setOrchestrationIdsMap,
  updateExistingOrchestration,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
